import React, { useState } from 'react'
import { Avatar, Radio, Tabs } from 'antd'
import TabPane from 'antd/lib/tabs/TabPane'
import CustomModal from 'components/global/CustomModal'
import ButtonSpinner from 'components/global/ButtonSpinner'
import Spinner from 'components/Spinner'
import { leagueAttendanceStore } from 'state/league/leagueAttendance'
import { leagueEventStore } from 'state/league/leagueEvent'
import {
  useLeagueDropInCheckout,
  useLeagueAdminDropIn,
  useAddDropInToFamily,
  getDropInFamilyMembers,
  DropInFamilyMember,
} from 'api/league/LeagueAttendance/leagueAttendance'
import { failureMessage, successMessage } from 'api/global'
import { getNameSignature } from 'helper/utility'
import LeagueParticipantAndDetails from './Common/LeagueParticipantAndDetails'
import DropInHistoryTab from './DropInHistoryTab'
import DropInAddKidForm from './DropInAddKidForm'
import DropInAddAdultForm from './DropInAddAdultForm'
import { Input } from 'antd'

type Step = 'main' | 'family-list' | 'add-kid' | 'add-adult'

const DropInDetailModal: React.FC = () => {
  const [tabKey, setTabKey] = useState('1')
  const [step, setStep] = useState<Step>('main')
  const [email, setEmail] = useState('')
  const [emailLoading, setEmailLoading] = useState(false)
  const [familyMembers, setFamilyMembers] = useState<DropInFamilyMember[]>([])

  const {
    dropInDetailModalOpen,
    dropInMode,
    setDropInDetailModalOpen,
    setCheckInOutModalOpen,
    selectedAttendee,
    setSelectedAttendee,
  } = leagueAttendanceStore(store => ({
    dropInDetailModalOpen: store.dropInDetailModalOpen,
    dropInMode: store.dropInMode,
    setDropInDetailModalOpen: store.setDropInDetailModalOpen,
    setCheckInOutModalOpen: store.setCheckInOutModalOpen,
    selectedAttendee: store.selectedAttendee,
    setSelectedAttendee: store.setSelectedAttendee,
  }))

  const { leagueData } = leagueEventStore(store => ({ leagueData: store.leagueData }))

  const { mutate: dropOut, isLoading: checkOutLoading } = useLeagueDropInCheckout()
  const { mutate: dropInCheckIn, isLoading: checkInLoading } = useLeagueAdminDropIn()
  const { mutate: addToFamily, isLoading: addToFamilyLoading } = useAddDropInToFamily()

  const resetAndClose = () => {
    setStep('main')
    setEmail('')
    setFamilyMembers([])
    setSelectedAttendee(null)
    setDropInDetailModalOpen(false)
    setCheckInOutModalOpen(true)
  }

  // ─── Checkout (final step for both flows) ───────────────────────────────────
  const performCheckOut = () => {
    if (!selectedAttendee?.checkInId) return failureMessage('No active check-in found')
    const adminId = localStorage.getItem('adminId') || ''
    dropOut(
      { email: 'admin@bamrec.com', checkInId: selectedAttendee.checkInId, checkOutAdminId: adminId || undefined },
      {
        onSuccess: () => { successMessage('Checked out successfully'); resetAndClose() },
        onError: () => failureMessage('Failed to check out'),
      },
    )
  }

  // ─── Check-in (re-check-in a dropped-out drop-in attendee) ──────────────────
  // Uses /drop-in endpoint where familyId is OPTIONAL.
  // The regular /check-in endpoint requires familyId which a pure drop-in may not have.
  const handleCheckIn = () => {
    if (!leagueData?._id || !selectedAttendee?._id) return
    dropInCheckIn(
      {
        leagueId: leagueData._id,
        dropInAttendeeId: selectedAttendee.dropInAttendeeId || selectedAttendee._id,
        familyId: selectedAttendee.familyId || undefined,
      },
      {
        onSuccess: () => { successMessage('Checked in successfully'); resetAndClose() },
        onError: () => failureMessage('Failed to check in'),
      },
    )
  }

  // ─── Flow 2: Search family by email ─────────────────────────────────────────
  const handleFindAccount = async () => {
    if (!email.trim()) return failureMessage('Enter a valid email')
    if (!leagueData?._id) return
    setEmailLoading(true)
    try {
      const members = await getDropInFamilyMembers({
        email: email.trim(),
        leagueId: leagueData._id,
        today: new Date().toISOString(),
      })
      // guardians excluded per spec — show kids + authorized adults only
      const filtered = (members || []).filter((m: DropInFamilyMember) => m.type !== 'guardian')
      setFamilyMembers(filtered)
    } catch {
      setFamilyMembers([])
    } finally {
      setEmailLoading(false)
      setStep('family-list')
    }
  }

  // ─── Flow 2: Select existing family member → add-to-family → checkout ───────
  const handleSelectMember = (member: DropInFamilyMember) => {
    if (member.status === 'checked-in') {
      failureMessage('This member is already checked in')
      return
    }
    if (!selectedAttendee?._id || !selectedAttendee?.checkInId) return
    const payload: any = {
      dropInAttendeeId: selectedAttendee._id,
      familyId: member.familyId,
      checkInId: selectedAttendee.checkInId,
      date: new Date().toISOString(),
      leagueId: leagueData?._id,
    }
    if (member.type === 'kid') payload.kidId = member._id
    else if (member.type === 'authorized-adult') payload.authorizedAdultId = member._id

    addToFamily(payload, {
      onSuccess: () => performCheckOut(),
      onError: () => failureMessage('Failed to link to family'),
    })
  }

  // ─── Flow 2: After creating a new kid/adult → add-to-family → checkout ──────
  const handleNewMemberCreated = (memberId: string, type: 'kid' | 'authorized-adult') => {
    if (!selectedAttendee?._id || !selectedAttendee?.checkInId || !familyMembers[0]?.familyId) {
      return failureMessage('Family not found — cannot link member')
    }
    const payload: any = {
      dropInAttendeeId: selectedAttendee._id,
      familyId: familyMembers[0].familyId,
      checkInId: selectedAttendee.checkInId,
      date: new Date().toISOString(),
      leagueId: leagueData?._id,
    }
    if (type === 'kid') payload.kidId = memberId
    else payload.authorizedAdultId = memberId

    addToFamily(payload, {
      onSuccess: () => performCheckOut(),
      onError: () => failureMessage('Failed to link to family'),
    })
  }

  // ─── Modal title per step ────────────────────────────────────────────────────
  const modalTitle = () => {
    if (step === 'family-list') return 'Which matches this profile?'
    if (step === 'add-kid') return 'Add a kid'
    if (step === 'add-adult') return 'Add an authorized adult'
    return dropInMode === 'check-in' ? 'Check in — drop-in attendee' : 'Drop-in details'
  }

  const mainIsWide = step === 'main' && dropInMode === 'check-out'

  return (
    <CustomModal
      openModal={dropInDetailModalOpen}
      isCloseModalCustom
      closeModal={resetAndClose}
      title={modalTitle()}
      width={mainIsWide ? 1000 : 660}
      footer={false}
    >
      {/* ── Main step ── */}
      {step === 'main' && (
        <Tabs defaultActiveKey={tabKey} onChange={setTabKey}>
          <TabPane tab="Drop-in" key="1">
            <div className="flex gap-20px">
              <div className="flex-1 relative" style={{ minHeight: '35rem' }}>
                <div className="pt-20px">
                  <h3 className="text-14 w-600 mb-20px">Drop-in attendee</h3>

                  {selectedAttendee && (
                    <div className="mb-20px">
                      {([
                        ['Name', selectedAttendee.name],
                        ['Organization', selectedAttendee.organization],
                        ['Team', selectedAttendee.team],
                        ['Division', selectedAttendee.division],
                        ['Check-in time', selectedAttendee.checkInDate],
                      ] as [string, string][])
                        .filter(([, v]) => v && v !== '—')
                        .map(([label, value]) => (
                          <div key={label} className="mb-10px">
                            <p className="text-12 opacity-60">{label}</p>
                            <p className="text-14 w-500">{value}</p>
                          </div>
                        ))}
                    </div>
                  )}
                  {/* Check-in mode — single action */}
                  {dropInMode === 'check-in' && (
                    <div style={{ maxWidth: 220 }}>
                      <button className="btn-prim w-full" onClick={handleCheckIn} disabled={checkInLoading}>
                        {checkInLoading ? <ButtonSpinner size={20} color="#fff" /> : 'Check In'}
                      </button>
                    </div>
                  )}

                  {/* Check-out mode — Flow 1 (link to family) FIRST, direct checkout second */}
                  {dropInMode === 'check-out' && (
                    <>
                      <h3 className="text-14 w-600 mb-10px">Link to a family account</h3>
                      <div className="flex gap-10px align-end mb-20px" style={{ maxWidth: 420 }}>
                        <div className="flex-1">
                          <label className="input-label">Parent's email</label>
                          <Input
                            className="custom-input-field w-full mt-6px"
                            placeholder="example@gmail.com"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            onPressEnter={handleFindAccount}
                          />
                        </div>
                        <button
                          className="btn-prim"
                          onClick={handleFindAccount}
                          disabled={emailLoading}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {emailLoading ? <ButtonSpinner size={20} color="#fff" /> : 'Find account'}
                        </button>
                      </div>

                      <div className="att-separator mb-20px" />
                      <div style={{ maxWidth: 220 }}>
                        <button className="btn-prim danger-bg w-full" onClick={performCheckOut} disabled={checkOutLoading}>
                          {checkOutLoading ? <ButtonSpinner size={20} color="#fff" /> : 'Check Out'}
                        </button>
                      </div>
                    </>
                  )}


                </div>
              </div>

              {selectedAttendee?.familyId ? (
                <div className="bg-dark-gray px-30px py-20px">
                  <LeagueParticipantAndDetails />
                </div>
              ) : null}
            </div>
          </TabPane>

          <TabPane tab="History" key="2">
            <DropInHistoryTab />
          </TabPane>
        </Tabs>
      )}

      {/* ── Family list step ── */}
      {step === 'family-list' && (
        <div className="pt-20px">
          {addToFamilyLoading ? (
            <div className="flex justify-center py-40px"><Spinner /></div>
          ) : familyMembers.length === 0 ? (
            /* Account not found */
            <div className="py-20px">
              <p className="text-14 w-500 mb-6px">No account found for this email.</p>
              <p className="text-12 opacity-60 mb-20px">
                Check out without linking, or add a new member below.
              </p>
              <div className="flex gap-10px flex-wrap">
                <button className="btn-prim danger-bg" onClick={performCheckOut} disabled={checkOutLoading}>
                  {checkOutLoading ? <ButtonSpinner size={20} color="#fff" /> : 'Check Out anyway'}
                </button>
                <button className="btn-borderless" onClick={() => setStep('main')}>Back</button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-14 w-600 mb-15px">Select the matching family member:</p>

              {familyMembers.map((member: DropInFamilyMember) => (
                <div
                  key={member._id}
                  className={`flex align-center gap-15px py-15px px-10px radius-8 border-btm ${member.status === 'checked-in' ? 'opacity-50' : 'pointer'}`}
                  onClick={() => member.status !== 'checked-in' && handleSelectMember(member)}
                >
                  <Avatar size={40} src={member.photo?.post}>
                    {getNameSignature(`${member.firstName} ${member.lastName}`)}
                  </Avatar>
                  <div className="flex-1">
                    <p className="text-14 w-500">{member.firstName} {member.lastName}</p>
                    <p className="text-12 opacity-60" style={{ textTransform: 'capitalize' }}>
                      {member.type?.replace('-', ' ')}
                    </p>
                  </div>
                  {member.status === 'checked-in' && (
                    <span className="text-12" style={{ color: '#2ba7ca' }}>Already checked in</span>
                  )}
                  {addToFamilyLoading && <Spinner />}
                </div>
              ))}

              <div className="mt-20px flex gap-10px flex-wrap">
                <button
                  className="btn-prim"
                  onClick={() => setStep('add-kid')}
                >
                  None of the above — Add a kid
                </button>
                <button
                  className="prim-sm-btn"
                  style={{ background: '#EBEDF1', color: '#000' }}
                  onClick={() => setStep('add-adult')}
                >
                  Add an authorized adult
                </button>
                <button className="btn-borderless" onClick={() => setStep('main')}>Back</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Add kid step (full form) ── */}
      {step === 'add-kid' && familyMembers[0]?.familyId && (
        <div className="pt-10px" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <DropInAddKidForm
            familyId={familyMembers[0].familyId}
            onSuccess={kidId => handleNewMemberCreated(kidId, 'kid')}
            onBack={() => setStep('family-list')}
            externalLoading={addToFamilyLoading || checkOutLoading}
          />
        </div>
      )}

      {/* ── Add authorized adult step (full form) ── */}
      {step === 'add-adult' && familyMembers[0]?.familyId && (
        <div className="pt-10px" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <DropInAddAdultForm
            familyId={familyMembers[0].familyId}
            prefillEmail={email}
            onSuccess={adultId => handleNewMemberCreated(adultId, 'authorized-adult')}
            onBack={() => setStep('family-list')}
            externalLoading={addToFamilyLoading || checkOutLoading}
          />
        </div>
      )}
    </CustomModal>
  )
}

export default DropInDetailModal
